/**
 * Shipping Rates Hook using TanStack Query
 * Simplified shipping rates management with automatic cache invalidation
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ShippingRatesResource } from '../../core/resources/shippingRates';
import { getGlobalApiClient } from './useApiQuery';
export function useShippingRatesQuery(options = {}) {
    const { sessionId, checkout, onSuccess, enabled = true } = options;
    const queryClient = useQueryClient();
    // Create shipping rates resource client
    const shippingRatesResource = useMemo(() => {
        try {
            return new ShippingRatesResource(getGlobalApiClient());
        }
        catch (error) {
            throw new Error('Failed to initialize shipping rates resource: ' +
                (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);
    // Use sessionId from options or checkout
    const effectiveSessionId = sessionId || checkout?.checkoutSession?.id;
    // Track if we've synced the initial selection from checkout
    const hasSyncedInitialSelectionRef = useRef(false);
    // Preview rates params - triggers preview query when set
    const [previewParams, setPreviewParams] = useState(null);
    // Main shipping rates query
    const { data: shippingRatesData, isLoading: isFetching, error: fetchError, refetch: refetchRates, } = useQuery({
        queryKey: ['shipping-rates', effectiveSessionId],
        queryFn: () => {
            if (!enabled)
                return Promise.resolve({ rates: [] });
            return shippingRatesResource.getShippingRates(effectiveSessionId);
        },
        enabled: enabled && !!effectiveSessionId,
        staleTime: 30000, // 30 seconds
        refetchOnWindowFocus: false,
        select: (data) => {
            // Order rates: free ones first, then by ascending amount
            const sortedRates = [...data.rates].sort((a, b) => {
                if (a.isFree && !b.isFree)
                    return -1;
                if (!a.isFree && b.isFree)
                    return 1;
                return (a.amount ?? 0) - (b.amount ?? 0);
            });
            return {
                ...data,
                rates: sortedRates,
            };
        },
    });
    // Get sorted shipping rates from query data
    const shippingRates = shippingRatesData?.rates;
    // Preview shipping rates query - invalidated by promotions mutations
    const { data: previewedRates, isLoading: isPreviewLoading, } = useQuery({
        queryKey: ['shipping-rates-preview', effectiveSessionId, previewParams?.countryCode, previewParams?.stateCode],
        queryFn: async () => {
            const response = await shippingRatesResource.previewShippingRates(effectiveSessionId, previewParams);
            return [...response.rates].sort((a, b) => {
                if (a.isFree && !b.isFree)
                    return -1;
                if (!a.isFree && b.isFree)
                    return 1;
                return (a.amount ?? 0) - (b.amount ?? 0);
            });
        },
        enabled: !!effectiveSessionId && !!previewParams,
        staleTime: 30000,
        refetchOnWindowFocus: false,
    });
    // Get selected rate from checkout data
    const checkoutSelectedRateId = checkout?.checkoutSession?.shippingRate?.id;
    // Set shipping rate mutation
    const setShippingRateMutation = useMutation({
        mutationFn: ({ rateId }) => {
            if (!effectiveSessionId) {
                throw new Error('No session ID available');
            }
            return shippingRatesResource.setShippingRate(effectiveSessionId, rateId);
        },
        onSuccess: async () => {
            // Invalidate both shipping rates and checkout queries
            if (effectiveSessionId) {
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ['shipping-rates', effectiveSessionId] }),
                    queryClient.invalidateQueries({ queryKey: ['checkout'] }),
                ]);
            }
            // Call onSuccess callback if provided
            if (onSuccess) {
                onSuccess();
            }
        },
    });
    // Select rate function
    const selectRate = useCallback(async (rateId) => {
        if (!enabled)
            return;
        await setShippingRateMutation.mutateAsync({ rateId });
    }, [enabled, setShippingRateMutation]);
    // Auto-select cheapest rate when:
    // 1. Rates are loaded
    // 2. No rate is currently selected in checkout
    // 3. We haven't already auto-selected
    useEffect(() => {
        if (shippingRates &&
            shippingRates.length > 0 &&
            !checkoutSelectedRateId &&
            !hasSyncedInitialSelectionRef.current &&
            !setShippingRateMutation.isPending) {
            // Find the cheapest shipping rate
            const cheapestRate = shippingRates.reduce((min, rate) => {
                // Prioritize isFree rates first
                if (rate.isFree && !min.isFree)
                    return rate;
                if (!rate.isFree && min.isFree)
                    return min;
                // If both are free or both are not free, compare by amount
                return rate.amount < min.amount ? rate : min;
            }, shippingRates[0]);
            console.log('[useShippingRatesQuery] Auto-selecting cheapest rate:', cheapestRate);
            // Mark as synced before making the call to prevent multiple calls
            hasSyncedInitialSelectionRef.current = true;
            // Auto-select the cheapest rate
            void selectRate(cheapestRate.id);
        }
    }, [shippingRates, checkoutSelectedRateId, selectRate, setShippingRateMutation.isPending]);
    // Reset sync flag when session changes
    useEffect(() => {
        hasSyncedInitialSelectionRef.current = false;
    }, [effectiveSessionId]);
    // Find the selected rate from shipping rates based on checkout data
    const selectedRate = useMemo(() => {
        if (!shippingRates || !checkoutSelectedRateId)
            return undefined;
        return shippingRates.find((rate) => rate.id === checkoutSelectedRateId);
    }, [shippingRates, checkoutSelectedRateId]);
    // Refetch function
    const refetch = useCallback(async () => {
        if (!enabled)
            return;
        await refetchRates();
    }, [enabled, refetchRates]);
    // Clear error function
    const clearError = useCallback(() => {
        // TanStack Query doesn't provide a direct way to clear errors
        // We can trigger a refetch which will clear the error
        void refetch();
    }, [refetch]);
    // Preview shipping rates for a country without updating session
    const previewRates = useCallback(async (countryCode, stateCode) => {
        if (!enabled || !effectiveSessionId)
            return [];
        setPreviewParams({ countryCode, stateCode });
        try {
            return await queryClient.fetchQuery({
                queryKey: ['shipping-rates-preview', effectiveSessionId, countryCode, stateCode],
                queryFn: async () => {
                    const response = await shippingRatesResource.previewShippingRates(effectiveSessionId, {
                        countryCode,
                        stateCode,
                    });
                    return [...response.rates].sort((a, b) => {
                        if (a.isFree && !b.isFree)
                            return -1;
                        if (!a.isFree && b.isFree)
                            return 1;
                        return (a.amount ?? 0) - (b.amount ?? 0);
                    });
                },
            });
        }
        catch (error) {
            console.error('[useShippingRatesQuery] Error previewing shipping rates:', error);
            return [];
        }
    }, [enabled, effectiveSessionId, queryClient, shippingRatesResource]);
    return {
        shippingRates,
        selectedRate,
        selectRate,
        isLoading: isFetching || setShippingRateMutation.isPending,
        error: (fetchError || setShippingRateMutation.error),
        clearError,
        refetch,
        previewRates,
        previewedRates,
        isPreviewLoading,
    };
}
